import { Dtos, SuretyAccountId, SuretyQuoteId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import { accountQueryKey } from "../../account/account.keys";
import { adminActionItemsQueryKey } from "../../action-item/action-item.keys";
import { bondFormFileUrlQueryKey, bondPreviewsQueryKey } from "../../bond/bond.keys";
import { bondRequestPreviewsQueryKey } from "../bond-request.keys";
import {
	invalidateBondRequestAfterIssue,
	invalidateBondRequestIfClosed,
	redirectToIssuedBond,
} from "../utils";

export const useCompleteAgentIssuedBondForm = (
	id: SuretyQuoteId,
	accountId: SuretyAccountId,
	upload: boolean,
) => {
	const queryClient = useQueryClient();
	const { post } = useRequest();

	const mutation = useMutation({
		meta: { redirectOnError: redirectToIssuedBond },
		mutationKey: ["uploadAgentIssuedBondForm", id],
		mutationFn: async (
			data:
				| Dtos.Admin.SuretyBondForm.Upload.AgentIssue.Request
				| Dtos.Admin.SuretyBondForm.Approve.AgentIssue.Request,
		) => {
			return await post(
				`/v2/admin/surety/accounts/${accountId}/quotes/${id}/bond-form/${upload ? "upload" : "approve"}/agent-issue`,
				data,
			);
		},
		onSuccess: () => {
			void invalidateBondRequestAfterIssue(queryClient, id);
			queryClient.invalidateQueries({ queryKey: bondRequestPreviewsQueryKey() });
			queryClient.invalidateQueries({ queryKey: accountQueryKey(accountId) });
			queryClient.invalidateQueries({ queryKey: bondPreviewsQueryKey() });
			queryClient.invalidateQueries({ queryKey: adminActionItemsQueryKey.all() });
			queryClient.invalidateQueries({ queryKey: bondFormFileUrlQueryKey(id) });
		},
		onSettled: invalidateBondRequestIfClosed(queryClient, id),
	});

	return {
		completeAgentIssuedBondForm: mutation.mutate,
		completeAgentIssuedBondFormIsLoading: mutation.isPending,
		completeAgentIssuedBondFormIsSuccess: mutation.isSuccess,
		resetCompleteAgentIssuedBondForm: mutation.reset,
	};
};
